import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Tree, Overlay } from '@blueprintjs/core';
import { withTranslation } from 'react-i18next/src/index';
import { withRouter } from 'react-router-dom';
import { H2 } from '@hobs/components/Headings';
import Toast from '@hobs/components/Toast';
import Icon from '@hobs/components/Icon';
import Confirm from '@hobs/components/Confirm';
import {
  RaisedButton,
  SquareButton,
  SquareGhostButton
} from '@hobs/components/Buttons';
import { getSor } from '@hobs/actions/sor';
import {
  getSorUnits,
  getSorUnit,
  addElementToExercise,
  updateUnit,
  resetSorUnit,
  getUnitStaticData,
  removeUnit
} from '../../../actions/sor';

import UnitDialog from '../../Units/UnitDialog';
import { buildTree, getUnitObject } from '../utils/tree';

import SorForm from './SorForm';
import Styles from './SorUnits.module.scss';

const DEFAULT_FILTER = 'Exercise';

class SorUnits extends React.PureComponent {
  state = {
    selected: null,
    filter: DEFAULT_FILTER,
    addElementToExercise: null,
    hasUnits: true,
    isLoading: true,
    addElementDisabled: false
  };

  componentDidMount() {
    const { getSorUnits } = this.props;

    getSorUnits('All').then(() => {
      const { units } = this.props;

      this.setState({
        hasUnits: units.length > 0
      });

      this.fetchUnits(this.state.filter);
    });
  }

  fetchUnits = filter => {
    const newState = {
      filter: filter
    };
    if (filter !== this.state.filter && !this.props.unit) {
      Object.assign(newState, {
        selected: null,
        addElementToExercise: null
      });
    }
    this.props.getSorUnits(filter).then(() => {
      const { units } = this.props;

      if (!units.length) {
        Object.assign(newState, {
          selected: null,
          addElementToExercise: null
        });
      }

      this.setState(newState);

      if (
        units.length &&
        !units.find(unit => unit.unitId === this.state.selected)
      ) {
        this.selectUnit(units[0]);
      }
    });
  };

  addElementToExercise = () => {
    const {
      addElementToExercise,
      t,
      getSorUnit,
      getUnitStaticData
    } = this.props;

    this.setState({
      addElementDisabled: true
    });

    addElementToExercise(this.state.addElementToExercise).then(response => {
      if (response.success) {
        Toast.success(t('Unit information updated successfully'));
        this.fetchUnits(this.state.filter);
        getSorUnit(this.state.addElementToExercise);
        getUnitStaticData(this.state.addElementToExercise).then(() => {
          this.setState({
            addElementToExercise: null,
            addElementDisabled: false
          });
        });
      } else if (response.payload) {
        Toast.warning(response.payload.message);
        this.setState({
          addElementDisabled: false
        });
      } else {
        Toast.warning(t('Cannot update Unit information'));
        this.setState({
          addElementDisabled: false
        });
      }
    });
  };

  selectUnit = unit => {
    const newState = {
      selected: unit.unitId,
      addElementToExercise: null,
      isLoading: false
    };

    if (unit.isCreated) {
      this.props.getSorUnit(unit.unitId);
      this.props.getUnitStaticData(unit.unitId);
    } else {
      this.props.resetSorUnit();
      Object.assign(newState, {
        addElementToExercise: unit.unitId
      });
    }

    this.setState(newState);
  };

  handleNodeClick = unit => {
    this.selectUnit(unit);
  };

  updateUnit = (values, { setSubmitting }) => {
    const { updateUnit, t } = this.props;

    const data = {
      ...values,
      likelyFrom: values.likelyFrom,
      likelyTo: values.likelyTo
    };

    updateUnit(data).then(response => {
      if (response.success) {
        Toast.success(t('Unit information updated successfully'));
        this.fetchUnits(this.state.filter);
        this.props.getUnitStaticData(data.unitId);
      } else if (response.payload) {
        Toast.warning(response.payload.message);
      } else {
        Toast.warning(t('Cannot update Unit information'));
      }

      setSubmitting(false);
    });
  };

  removeUnit = () => {
    const { removeUnit, t } = this.props;
    Confirm(
      this.props.t('Remove from exercise'),
      this.props.t(
        'Are you sure you want to remove this unit from the Exercise?'
      ),
      () => {
        removeUnit(this.state.selected).then(response => {
          if (response.success) {
            Toast.success(t('Unit information updated successfully'));
            this.setState({
              addElementToExercise: this.state.selected
            });
            this.fetchUnits(this.state.filter);
          } else if (response.payload) {
            Toast.warning(response.payload.message);
          } else {
            Toast.warning(t('Cannot update Unit information'));
          }
        });
      }
    );
  };

  getFilterButtons = () => {
    const ButtonAllUnits =
      this.state.filter === DEFAULT_FILTER ? SquareGhostButton : SquareButton;
    const ButtonUATOE =
      this.state.filter === DEFAULT_FILTER ? SquareButton : SquareGhostButton;

    return (
      <>
        <ButtonAllUnits size="small" onClick={() => this.fetchUnits('All')}>
          {this.props.t('All units')}
        </ButtonAllUnits>
        <ButtonUATOE size="small" onClick={() => this.fetchUnits('Exercise')}>
          {this.props.t('Units added to exercise')}
        </ButtonUATOE>
      </>
    );
  };

  getUnitCreatePathName() {
    return `${this.props.location.pathname.replace('/unit', '')}/unit`;
  }

  getUnitsForUnitDialog = units => {
    if (!Array.isArray(units)) {
      return [];
    }

    return units.map(unit => {
      return {
        title: unit.unitName,
        ...unit,
        units: this.getUnitsForUnitDialog(unit.units)
      };
    });
  };

  canAddUnitToExercise = unit => {
    if (!unit.parentUnit) {
      return true;
    }

    if (unit.parentUnit.statuses) {
      const status = unit.parentUnit.statuses[0].type;
      return status !== 0;
    }

    return false;
  };

  onUnitCreate = unit => {
    this.setState({
      hasUnits: true
    });

    if (this.canAddUnitToExercise(unit)) {
      this.props.addElementToExercise(unit.unitId).then(response => {
        if (response.success) {
          this.fetchUnits(this.state.filter);
        } else if (response.payload) {
          Toast.warning(response.payload.message);
        }
      });
    } else {
      this.fetchUnits(this.state.filter);
    }
  };

  sortUnits(units) {
    if (!Array.isArray(units)) {
      return [];
    }

    const sortedUnits = units.sort((a, b) =>
      a.unitName.toLowerCase() > b.unitName.toLowerCase()
        ? 1
        : a.unitName.toLowerCase() < b.unitName.toLowerCase()
          ? -1
          : 0
    );

    return sortedUnits.map(unit => {
      return {
        ...unit,
        units: this.sortUnits(unit.units)
      };
    });
  }

  render() {
    const {
      t,
      user,
      unit,
      units,
      responsiblePerson,
      disabled,
      canCreateUnit,
      sorPhaseType
    } = this.props;
    const unitsTree = buildTree(
      getUnitObject(this.state.selected),
      this.sortUnits(units)
    );
    const { hasUnits, isLoading, addElementDisabled } = this.state;

    return (
      <div className={Styles.units}>
        {user && canCreateUnit && (
          <UnitDialog
            partyId={user.PartyId}
            units={this.getUnitsForUnitDialog(units)}
            onUnitCreate={this.onUnitCreate}
          />
        )}
        <>
          <div className={Styles.left}>
            <div className={Styles.header}>
              {this.getFilterButtons()}
              {canCreateUnit && (
                <RaisedButton
                  iconLeft={<Icon icon="plus" />}
                  to={this.getUnitCreatePathName()}
                >
                  {t('Add new')}
                </RaisedButton>
              )}
            </div>
            <div className={Styles.tree}>
              {unitsTree.length > 0 && !isLoading && (
                <Tree
                  contents={unitsTree}
                  onNodeClick={this.handleNodeClick}
                />
              )}
            </div>
          </div>
          <div className={Styles.right}>
            {units.length === 0 && canCreateUnit && !hasUnits && (
              <div className={Styles['units--empty']}>
                <div className={Styles['units--empty--message']}>
                  {t('There are no elements in the list.')}
                </div>
                <RaisedButton
                  iconLeft={<Icon icon="Plus" />}
                  to={this.getUnitCreatePathName()}
                >
                  {t('Add new element')}
                </RaisedButton>
              </div>
            )}

            {!(units.length === 0 && canCreateUnit && !hasUnits) && (
              <>
                <H2>{t('Sending Nation unit information')}</H2>
                {(unit || this.state.addElementToExercise) && !isLoading && sorPhaseType && (
                  <div className={Styles.overlayWrapper}>
                    <SorForm
                      unit={unit}
                      updateUnit={this.updateUnit}
                      removeUnit={this.removeUnit}
                      responsiblePerson={responsiblePerson}
                      disabled={disabled}
                      sorPhase={sorPhaseType}
                    />
                    {!disabled && (
                      <Overlay
                        usePortal={false}
                        isOpen={!!this.state.addElementToExercise}
                        backdropClassName={Styles.overlayBackdrop}
                      >
                        <RaisedButton
                          iconLeft={<Icon icon="Plus" />}
                          onClick={this.addElementToExercise}
                          disabled={addElementDisabled}
                        >
                          {t('Add element to exercise')}
                        </RaisedButton>
                      </Overlay>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </>
      </div>
    );
  }
}

SorUnits.propTypes = {
  t: PropTypes.func,
  location: PropTypes.object,
  match: PropTypes.object,
  history: PropTypes.object,
  units: PropTypes.arrayOf(PropTypes.object),
  unit: PropTypes.object,
  responsiblePerson: PropTypes.arrayOf(PropTypes.object),
  getSorUnits: PropTypes.func,
  getSorUnit: PropTypes.func,
  addElementToExercise: PropTypes.func,
  updateUnit: PropTypes.func,
  resetSorUnit: PropTypes.func,
  getUnitStaticData: PropTypes.func,
  removeUnit: PropTypes.func,
  user: PropTypes.object,
  disabled: PropTypes.bool,
  canCreateUnit: PropTypes.bool,
  getSor: PropTypes.func,
  sorPhaseType: PropTypes.string,
  sorId: PropTypes.string
};

const mapStateToProps = state => {
  return {
    units: state.sor.units,
    unit: state.sor.unit,
    responsiblePerson: state.sor.responsiblePerson,
    user: state.auth.user,
    sorPhaseType: (state.sor && state.sor.sorSummary) ? state.sor.sorSummary.phaseType : undefined
  };
};
const mapDispatchToProps = {
  getSorUnits,
  getSorUnit,
  addElementToExercise,
  updateUnit,
  resetSorUnit,
  getUnitStaticData,
  removeUnit,
  getSor
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withTranslation()(SorUnits))
);
